import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { CopyButton } from '@/components/shared/copy-button';
import { EmptyState } from '@/components/shared/empty-state';
import { FormField } from '@/components/shared/form-field';
import { TableSkeleton } from '@/components/shared/loading';
import { LoadingButton } from '@/components/shared/loading-button';
import { SectionCard } from '@/components/shared/section-card';
import { SimpleSelect } from '@/components/shared/simple-select';
import { ToneBadge } from '@/components/shared/tone-badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import type { ApiKey } from '@/lib/types';
import { useToast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { useAppCtx } from './app-layout';

export function ApiKeys() {
  const { app, env } = useAppCtx();
  const qc = useQueryClient();
  const [kind, setKind] = useState<'sdk' | 'server'>('sdk');
  const [name, setName] = useState('');
  const [created, setCreated] = useState<{ key: string; kind: string } | null>(null);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);
  const toast = useToast();

  const keys = useQuery({
    queryKey: ['keys', app.id, env],
    queryFn: () => api<{ data: ApiKey[] }>(`/admin/v1/apps/${app.id}/api-keys`, { query: { environment: env } }),
  });
  const create = useMutation({
    mutationFn: () => api<{ key: string; kind: string }>(`/admin/v1/apps/${app.id}/api-keys`, { method: 'POST', body: { kind, environment: env, name } }),
    onSuccess: (k) => { setCreated(k); setName(''); void qc.invalidateQueries({ queryKey: ['keys', app.id] }); },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/admin/v1/apps/${app.id}/api-keys/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast(`Revoked ${revoking?.name ?? 'key'}`);
      closeRevoke();
      void qc.invalidateQueries({ queryKey: ['keys', app.id] });
    },
  });
  const closeRevoke = () => { setRevoking(null); revoke.reset(); };

  return (
    <div className="space-y-6">
      <SectionCard title={<>New key for <span className="capitalize">{env}</span></>} description="Keys are scoped to this application and environment.">
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-[12rem_1fr_auto] sm:items-end">
            <FormField label="Kind">
              <SimpleSelect value={kind} onValueChange={(v) => setKind(v as 'sdk' | 'server')} options={[{ value: 'sdk', label: 'SDK (public)' }, { value: 'server', label: 'Server (secret)' }]} />
            </FormField>
            <FormField label="Name" htmlFor="key-name"><Input id="key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === 'sdk' ? 'iOS app 3.x' : 'orders-service'} /></FormField>
            <LoadingButton disabled={!name.trim()} loading={create.isPending} onClick={() => create.mutate()}><Plus /> Create key</LoadingButton>
          </div>
          <p className="text-xs text-muted-foreground">
            {kind === 'sdk'
              ? 'SDK keys ship inside your app binary: they can only register their own device. Being public, they stay copyable from the list.'
              : <>Server keys can send notifications. Keep them on your backend only — never in an app. To send from Node.js, <Link to="/resources" className="underline underline-offset-2">download the server SDK</Link>.</>}
          </p>
          {created && (
            <Alert className="border-success/40 bg-success/10">
              <ShieldAlert className="text-success" />
              <AlertTitle>
                {created.kind === 'server'
                  ? "Copy this server key now — it won't be shown again."
                  : 'SDK key created. You can copy it from the list below at any time.'}
              </AlertTitle>
              <AlertDescription>
                <div className="mt-2 flex w-full gap-2"><Input readOnly value={created.key} className="bg-background font-mono text-xs" /><CopyButton value={created.key} /></div>
              </AlertDescription>
            </Alert>
          )}
        </div>
      </SectionCard>

      <SectionCard title={<>Keys for <span className="capitalize">{env}</span></>} flush>
        {keys.isLoading ? <TableSkeleton rows={3} /> : keys.data?.data.length === 0 ? <EmptyState icon={KeyRound} title={`No ${env} keys yet`} hint="Create an SDK key for your app and a server key for your backend." /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-5">Name</TableHead><TableHead>Kind</TableHead><TableHead>Environment</TableHead><TableHead>Prefix</TableHead>
                <TableHead>Last used</TableHead><TableHead>Created</TableHead><TableHead className="pr-5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.data?.data.map((k) => (
                <TableRow key={k.id} className={cn(k.revoked_at && 'opacity-50')}>
                  <TableCell className="pl-5 font-medium">{k.name}</TableCell>
                  <TableCell><ToneBadge tone={k.kind === 'server' ? 'amber' : 'blue'}>{k.kind}</ToneBadge></TableCell>
                  <TableCell className="capitalize">{k.environment}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs">{k.key_prefix}…</span>
                      {k.key && !k.revoked_at && <CopyButton value={k.key} label={false} />}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(k.last_used_at)}</TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(k.created_at)}</TableCell>
                  <TableCell className="pr-5 text-right">
                    {k.revoked_at ? <ToneBadge>revoked</ToneBadge> : (
                      <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => setRevoking(k)}>Revoke</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <Dialog open={!!revoking} onOpenChange={(open) => { if (!open && !revoke.isPending) closeRevoke(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke {revoking?.name}?</DialogTitle>
            <DialogDescription>
              {revoking?.kind === 'server'
                ? 'Backends using this server key will get 401 errors and can no longer send notifications.'
                : 'App builds that ship this SDK key will get 401 errors and can no longer register devices.'}
              {' '}This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {revoking && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-sm">
              <ToneBadge tone={revoking.kind === 'server' ? 'amber' : 'blue'}>{revoking.kind}</ToneBadge>
              <span className="capitalize text-muted-foreground">{revoking.environment}</span>
              <span className="font-mono text-xs">{revoking.key_prefix}…</span>
            </div>
          )}
          {revoke.isError && <p className="text-sm text-destructive">{revoke.error.message}</p>}
          <DialogFooter>
            <Button variant="outline" disabled={revoke.isPending} onClick={closeRevoke}>Cancel</Button>
            <LoadingButton variant="destructive" loading={revoke.isPending} onClick={() => revoking && revoke.mutate(revoking.id)}>Revoke key</LoadingButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
